import { gettext } from '@eventbrite/i18n';
import {
    getNavigatorLastRecentViewsList,
    VIEWS_KEY,
} from '@eventbrite/personalization';
import isEmpty from 'lodash/isEmpty';
import type {
    bboxShape,
    Event,
    EventMapScopedProps,
    LocationData,
} from './types';
import { ChildEventProps, SALES_STATUS } from './types';

export const eventIsOnSale = (event: ChildEventProps) =>
    (event?.is_sales_ended === false && event?.is_sold_out === false) ||
    (event &&
        'sales_status' in event &&
        event?.sales_status === SALES_STATUS.ON_SALE);

export const eventHasUnknownSalesStatus = (event: ChildEventProps) =>
    event &&
    'sales_status' in event &&
    event?.sales_status === SALES_STATUS.DEFAULT;

export const eventIsSalesEnded = (event: ChildEventProps) =>
    (event && 'is_sales_ended' in event && event?.is_sales_ended == true) ||
    (event &&
        'sales_status' in event &&
        event?.sales_status === SALES_STATUS.SALES_ENDED);

export const eventIsSoldOut = (event: ChildEventProps) =>
    (event && 'is_sold_out' in event && event?.is_sold_out == true) ||
    (event &&
        'sales_status' in event &&
        event?.sales_status === SALES_STATUS.SOLD_OUT);

export const getSalesStatus = (event: ChildEventProps) => {
    if (event?.sales_status) {
        return event.sales_status;
    } else {
        return eventIsOnSale(event)
            ? SALES_STATUS.ON_SALE
            : SALES_STATUS.DEFAULT;
    }
};

export const getPurchasableEvents = (childEvents: ChildEventProps[] | null) => {
    return childEvents?.filter(eventIsOnSale);
};

export const findFirstPurchasableEvent = (
    childEvents: ChildEventProps[] | null,
) => {
    if (!childEvents) return null;
    const availableEvents: ChildEventProps[] =
        childEvents.filter(eventIsOnSale);
    return availableEvents[0] ?? null;
};

export const getEventChildById = (
    childEvents: ChildEventProps[] | null,
    eventId: string,
) => childEvents?.find((event) => event.id === eventId) || {};

export const isYTShortVerticalVideo = (url?: string) => {
    return url && isYoutubeURL(url) && url.includes('short');
};

export const isYTHorizontalVideoURL = (url: string) => {
    return isYoutubeURL(url) && !url.includes('short');
};

const isYoutubeURL = (url: string) => {
    const pattern = /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/;
    return pattern.test(url);
};

export const buildSearchLocation = (location: LocationData = {}) => {
    const { placeId, bbox, isOnline, longitude, latitude } = location;
    if (isEmpty(bbox) && longitude && latitude) {
        location.bbox = buildBBox({ latitude, longitude });
    }
    const searchOptions = {
        places: placeId ? [placeId] : undefined,
        online_events_only: isOnline ? isOnline : undefined,
        latitude: latitude,
        longitude: longitude,
        bbox: location?.bbox ? concatLocationBBox(location?.bbox) : undefined,
    };
    return searchOptions;
};

export const buildBBox = ({
    latitude,
    longitude,
}: {
    latitude: number;
    longitude: number;
}) => ({
    sw: { lng: longitude - 0.4, lat: latitude - 0.3 },
    ne: { lng: longitude + 0.4, lat: latitude + 0.3 },
});

export const concatLocationBBox = (bbox: bboxShape) =>
    `${bbox.sw?.lng},${bbox.sw?.lat},${bbox.ne?.lng},${bbox.ne?.lat}`;

export const updateRecentViews = (event: Event) => {
    const eventsToStore: string[] = [
        event.id,
        ...getNavigatorLastRecentViewsList(9),
    ];
    const dedupedEventsToStore = [...new Set(eventsToStore)];
    localStorage.setItem(VIEWS_KEY, dedupedEventsToStore.toString());
};

interface SearchLocation {
    places?: string[];
    online_events_only?: boolean;
    latitude?: number;
    longitude?: number;
    bbox?: string;
}

export const haversineDistance = (
    lat1: number,
    long1: number,
    lat2: number,
    long2: number,
): number => {
    const earthRadius = 3958.8;

    const toRadians = (degree: number) => degree * (Math.PI / 180);

    const deltaLatitude = toRadians(lat2 - lat1);
    const deltaLongitude = toRadians(long2 - long1);

    const haversineLat =
        Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2);
    const haversineLon =
        Math.sin(deltaLongitude / 2) * Math.sin(deltaLongitude / 2);
    const latitudeCosine =
        Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2));

    const haversineFormula = haversineLat + latitudeCosine * haversineLon;

    const centralAngle =
        2 *
        Math.atan2(
            Math.sqrt(haversineFormula),
            Math.sqrt(1 - haversineFormula),
        );

    const distanceInMiles = earthRadius * centralAngle;

    return Math.round(distanceInMiles);
};

export const getDistanceString = (
    distance: number,
    country?: string,
): string => {
    const multiplierForKm = 1.60934;
    if (country && (country == 'USA' || country == 'United States')) {
        return gettext('%(distance)s miles', {
            distance,
        }).toString();
    } else {
        const distanceInKm = Math.round(distance * multiplierForKm);
        return gettext('%(distanceInKm)s km', {
            distanceInKm,
        }).toString();
    }
};

export const areLocationCoordinatesAvailable = (
    eventMapLayout: EventMapScopedProps,
    searchLocation: SearchLocation,
): boolean => {
    return (
        eventMapLayout.location?.latitude !== 0 &&
        eventMapLayout.location?.longitude !== 0 &&
        searchLocation?.latitude !== 0 &&
        searchLocation?.longitude !== 0
    );
};
